import * as THREE from 'three';
import { raycastTerrain } from './terrain';

// Z-aware targeting (OXIDE_SPEC.md section 3). Turns a cursor ray into a world AIM POINT that the
// ballistics muzzle fires toward. We resolve where the ray lands (first terrain hit, else a far
// Z-band fallback), then apply a SUBTLE aim-assist: if a candidate target sits within a small
// angular cone of the ray and near the resolved depth, the aim point is nudged toward it. Never a
// hard lock, just "help with your bullets". The caller supplies the candidate list (it knows the
// live targets) and reads back the resolved point to compute the firing direction from the muzzle.

// All tunables for aim resolution + assist. Centralized per the project rule.
export const TARGETING = {
  fallbackDepth: 80, // if the ray misses terrain, resolve the aim point this far down the ray (the Z-band)
  maxRange: 1200, // how far to march terrain before giving up to the fallback depth
  // Aim-assist cone: a candidate within this angle of the ray (and near the resolved depth) pulls
  // the aim point toward it. Small, so it only ever helps a near-miss, never grabs across the screen.
  assistConeDeg: 3.5, // half-angle of the assist cone
  assistStrength: 0.35, // BASE 0..1 lerp of the aim point toward the target center (subtle, never 1)
  assistDepthSlack: 14, // a candidate counts only if its depth along the ray is within this of the resolved depth
  assistMaxDepth: 400, // ignore candidates further than this along the ray (no cross-map snapping)
  // DISTANCE-SCALED assist (aim overhaul #10): long shots get more help than point-blank ones -- the
  // strength ramps from minScale x base up close to maxScale x base by farDepth metres.
  assistFarDepth: 80,
  assistMinScale: 0.6,
  assistMaxScale: 1.5,
} as const;

// A potential target the assist can nudge toward. pos is the world center; radius sizes the body
// so a near-miss inside the silhouette still assists. id is opaque (passed nowhere by targeting).
export interface AimCandidate {
  pos: THREE.Vector3;
  radius: number;
  id?: unknown;
}

export interface AimOpts {
  fallbackDepth?: number; // override the miss-the-world resolve depth (e.g. for a specific Z-band)
  assist?: boolean; // enable aim-assist (default true). Off for e.g. ADS purists or a debug shot
  // A surface point ALREADY resolved by the caller's richer world hit-test (walls, buildables, trees,
  // nodes, barrels, animals -- everything bullets collide with). When set it REPLACES the terrain-only
  // cast below, so the gun aims at the exact point under the cursor instead of the ground behind it
  // (owner 2026-07-13: "3rd person aiming needs to be FAR more intelligent"). Assist still applies.
  surface?: THREE.Vector3 | null;
}

const _toTarget = new THREE.Vector3();
const _resolved = new THREE.Vector3();
const _dirN = new THREE.Vector3();

// Resolve the cursor ray to a world aim point. rayDir need not be normalized. candidates may be
// empty. Returns `out` (filled in place) so the caller can reuse one vector with no allocation.
export function resolveAim(
  rayOrigin: THREE.Vector3,
  rayDir: THREE.Vector3,
  candidates: ReadonlyArray<AimCandidate>,
  out: THREE.Vector3,
  opts?: AimOpts,
): THREE.Vector3 {
  _dirN.copy(rayDir).normalize();

  // (1) Where does the cursor land in the world? The caller's pre-resolved surface point when given
  // (the full bullet-collision world: cover, builds, props, bodies); else the first terrain hit along
  // the ray; if the ray never meets anything (aimed at the sky, or past the meshed island), resolve to
  // a far point down the ray at the fallback depth. That far point IS the Z-band the spec describes:
  // the height the cursor is aiming at when there is no surface to stop on.
  if (opts?.surface) _resolved.copy(opts.surface);
  else {
    const ground = raycastTerrain(
      rayOrigin.x, rayOrigin.y, rayOrigin.z,
      _dirN.x, _dirN.y, _dirN.z,
      TARGETING.maxRange,
    );
    const fallback = opts?.fallbackDepth ?? TARGETING.fallbackDepth;
    if (ground) _resolved.set(ground.x, ground.y, ground.z);
    else _resolved.copy(rayOrigin).addScaledVector(_dirN, fallback);
  }

  out.copy(_resolved);
  if (opts?.assist === false || candidates.length === 0) return out;

  // (2) Subtle aim-assist. Depth along the ray to the resolved point, so we only consider targets at
  // roughly the depth the player is actually aiming (a target far behind the wall they aimed at does
  // not get the help).
  const resolvedDepth = _resolved.clone().sub(rayOrigin).dot(_dirN);
  const cosCone = Math.cos(THREE.MathUtils.degToRad(TARGETING.assistConeDeg));

  let best: AimCandidate | null = null;
  let bestScore = -Infinity; // most on-axis candidate inside the cone + depth window wins
  let bestDepth = 0;

  for (const cand of candidates) {
    _toTarget.copy(cand.pos).sub(rayOrigin);
    const depth = _toTarget.dot(_dirN); // distance along the ray to the target's nearest plane
    if (depth <= 0 || depth > TARGETING.assistMaxDepth) continue; // behind us / too far to snap to
    if (Math.abs(depth - resolvedDepth) > TARGETING.assistDepthSlack + cand.radius) continue;

    const dist = _toTarget.length() || 1;
    const cosA = depth / dist; // cosine of the angle between the ray and the target direction
    // Inside the cone, OR the ray already grazes the body (a near-miss within the silhouette).
    const angularSlack = cand.radius / Math.max(depth, 1); // body subtends this much extra angle
    if (cosA < cosCone - angularSlack) continue;

    if (cosA > bestScore) { bestScore = cosA; best = cand; bestDepth = depth; }
  }

  if (best) {
    // Nudge the aim point a fraction of the way toward the target center, MORE at range (a long shot on
    // a small silhouette earns more help than a point-blank one). Subtle: never a lock -- aim still
    // misses if you are way off.
    const scale = Math.min(TARGETING.assistMaxScale, Math.max(TARGETING.assistMinScale, TARGETING.assistMinScale + bestDepth / TARGETING.assistFarDepth));
    out.lerp(best.pos, Math.min(0.65, TARGETING.assistStrength * scale));
  }
  return out;
}
